import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import VisionAireLogo from '@/components/VisionAireLogo';
import API, { formatApiError } from '@/lib/api';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await API.post('/auth/forgot-password', { email });
            setSent(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="forgot-password-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                        <VisionAireLogo size={56} />
                    </div>
                    <h1 className="text-2xl font-bold text-zinc-900 font-['Outfit']">Reset Your Password</h1>
                    <p className="text-sm text-zinc-500 mt-1">Enter your email and we'll send you a reset link</p>
                </div>

                <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-8">
                    {sent ? (
                        <div className="text-center py-4" data-testid="reset-sent-confirmation">
                            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                            <h2 className="text-lg font-semibold text-zinc-900 mb-2">Check Your Email</h2>
                            <p className="text-sm text-zinc-600 mb-6">
                                If an account exists for <strong>{email}</strong>, we've sent a password reset link. Check your inbox (and spam folder).
                            </p>
                            <Link to="/login">
                                <Button className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl" data-testid="back-to-login-btn">
                                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg" data-testid="forgot-error">{error}</div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-zinc-700 mb-1.5">Email Address</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                                    <Input
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="pl-10 rounded-xl"
                                        data-testid="forgot-email-input"
                                    />
                                </div>
                            </div>
                            <Button
                                type="submit"
                                disabled={loading || !email}
                                className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold rounded-xl h-11"
                                data-testid="forgot-submit-btn"
                            >
                                {loading ? 'Sending...' : 'Send Reset Link'}
                            </Button>
                        </form>
                    )}
                </div>

                <div className="text-center mt-6">
                    <Link to="/login" className="text-sm text-zinc-500 hover:text-zinc-700">
                        <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Back to Login
                    </Link>
                </div>
            </div>
        </div>
    );
}
